import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SEO from '@/components/SEO';
import cannafaxLogo from '@/assets/cannafax-logo-clean.png';
import AuthLoadingSkeleton from '@/components/AuthLoadingSkeleton';
import { PersonaType, isSellerPersona } from '@/hooks/useUserPersona';
import { resolveLandingPath, companyHasInventory, deskSetupGraduated } from '@/lib/landing';
import { queueDeferredToast } from '@/hooks/useDeferredToast';
import { useQueryClient } from '@tanstack/react-query';
import { headerDataQueryKey, headerDataQueryFn } from '@/hooks/useHeaderData';
import { onboardingGateQueryKey, onboardingGateQueryFn } from '@/hooks/useOnboardingGate';
import { markPendingLegalConsent, flushPendingLegalConsent } from '@/lib/legalAcceptance';
import { SIGNUP_PLAN_STORAGE_KEY } from '@/lib/signupPlan';
import { readStoredReferralCode, readStoredReferrerName, readStoredReferralSource } from '@/hooks/useReferralProgram';
import { Gift } from 'lucide-react';
import { usePlanCatalog, formatPlanPrice } from '@/hooks/usePlanCatalog';
import { LAUNCH_BADGE_LABEL, LAUNCH_NOTICE, FREE_FOREVER_LABEL } from '@/hooks/useLaunchOffer';

const invitationTokenParam = (params: URLSearchParams) => params.get('invitation');

const Auth = () => {
  const { user, loading, initializing, signInWithGoogle, signInWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const queryReturnUrl = searchParams.get('returnUrl');
  // A plan carried in from the plans page, so signup doesn't re-ask which plan they want.
  const queryPlanKey = searchParams.get('plan');
  const fromShare = searchParams.get('from') === 'share';
  const modeParam = searchParams.get('mode');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { catalog: planCatalog } = usePlanCatalog();
  /* Confirm the plan they picked so the intent isn't lost at the signup gate.
     Invitations win (a joiner inherits their company's plan); unknown keys show nothing. */
  const selectedPlan =
    queryPlanKey && !invitationTokenParam(searchParams)
      ? planCatalog.plans.find((p) => p.key === queryPlanKey)
      : undefined;
  // Buyer invites credit the buyer but carry the normal trial — never the 60-day Guest Pass.
  const [isBuyerInvite] = useState(() => readStoredReferralSource() === 'buyer_invite');
  const [referralOfferFrom] = useState<string | null>(() =>
    readStoredReferralCode() ? (readStoredReferrerName() ?? 'your host') : null,
  );
  const [callbackWaitExpired, setCallbackWaitExpired] = useState(false);
  const hasRedirected = useRef(false);
  const oauthError = searchParams.get('error_description') || searchParams.get('error');
  const hasOAuthCallbackParams =
    searchParams.has('code') ||
    searchParams.has('access_token') ||
    searchParams.has('refresh_token') ||
    window.location.hash.includes('access_token');
  const shouldHoldCallbackLoader = hasOAuthCallbackParams && !user && !callbackWaitExpired && !oauthError;
  
  // Token-based invitation flow: capture invitation token from URL
  const invitationToken = searchParams.get('invitation');
  const invitationType = searchParams.get('type');

  // Two intents, one route. Account creation captures consent through the
  // 'Agree and proceed' button itself; returning sign-in does not (a post-auth
  // gate covers any missing record).
  const isSignupIntent = modeParam === 'signup' || fromShare || !!invitationToken;
  const consentRequired = isSignupIntent;
  
  // On mount, store invitation token and/or returnUrl for persistence across OAuth redirect
  useEffect(() => {
    if (invitationToken) {
      console.log('[Auth] Storing invitation token to localStorage');
      localStorage.setItem('pendingInvitationToken', invitationToken);
      localStorage.setItem('pendingInvitationType', invitationType || 'company');
    }
    if (queryReturnUrl && !invitationToken) {
      console.log('[Auth] Storing returnUrl to localStorage:', queryReturnUrl);
      localStorage.setItem('authReturnUrl', queryReturnUrl);
    }
    /* A plan picked on the plans page before signing up. Stored the same way the return
       address is, so it survives the Google/Microsoft redirect; onboarding consumes and
       clears it. Invitations win: a joiner inherits their company's plan. */
    if (queryPlanKey && !invitationToken) {
      localStorage.setItem(SIGNUP_PLAN_STORAGE_KEY, queryPlanKey);
    }
    if (invitationToken) {
      localStorage.removeItem(SIGNUP_PLAN_STORAGE_KEY);
    }
  }, [queryReturnUrl, queryPlanKey, invitationToken, invitationType]);

  // OAuth callback can briefly report no user before session exchange completes.
  // Keep a stable loader for a short grace window to avoid login-form flashing.
  useEffect(() => {
    if (!hasOAuthCallbackParams) {
      setCallbackWaitExpired(false);
      return;
    }

    setCallbackWaitExpired(false);
    const timer = window.setTimeout(() => setCallbackWaitExpired(true), 6000);
    return () => window.clearTimeout(timer);
  }, [hasOAuthCallbackParams]);




  useEffect(() => {
    const redirectTo = (path: string) => {
      /* Carry the plan picked on the plans page in the address too, so onboarding reads
         the choice even if browser storage was lost across the sign-in hop. */
      const carried = path === '/onboarding' && !localStorage.getItem('pendingInvitationToken')
        ? localStorage.getItem(SIGNUP_PLAN_STORAGE_KEY)
        : null;
      navigate(carried ? `${path}?plan=${encodeURIComponent(carried)}` : path, { replace: true });
    };

    const checkUserAndRedirect = async () => {
      
      if (loading || initializing) return;
      if (!user) return;
      if (hasRedirected.current) return;
      hasRedirected.current = true;

      // Record the click-accepted legal agreements captured on this page before
      // the OAuth redirect. Fire-and-forget: never blocks sign-in.
      flushPendingLegalConsent();

      const storedUrl = localStorage.getItem('authReturnUrl');

      // Check for pending menu invitation token
      const pendingToken = localStorage.getItem('pendingInvitationToken');
      const pendingType = localStorage.getItem('pendingInvitationType');
      
      if (pendingToken && pendingType === 'menu') {
        console.log('[Auth] Pending menu invitation found, auto-accepting...');
        localStorage.removeItem('pendingInvitationToken');
        localStorage.removeItem('pendingInvitationType');
        localStorage.removeItem('authReturnUrl');
        
        try {
          const { data, error } = await supabase.functions.invoke('accept-menu-invitation', {
            body: { token: pendingToken },
          });

          if (error || data?.error) {
            console.error('[Auth] Auto-accept failed:', error || data?.error);
            redirectTo('/supplier-menus');
            return;
          }

          const companyId = data?.company_id;
          const menuIds = data?.menu_ids;

          if (menuIds?.length > 0 && companyId) {
            redirectTo(`/menu/${companyId}/${menuIds[0]}`);
          } else if (companyId) {
            redirectTo(`/menu/${companyId}`);
          } else {
            redirectTo('/supplier-menus');
          }
        } catch (err) {
          console.error('[Auth] Auto-accept error:', err);
          redirectTo('/supplier-menus');
        }
        return;
      }

      // Pending COMPANY invitation — route directly to /onboarding so the
      // invitation lookup + accept flow runs (and so an invited user with a
      // blank profile never lands on a protected page first).
      if (pendingToken && pendingType === 'company') {
        console.log('[Auth] Pending company invitation found, routing to onboarding');
        localStorage.removeItem('authReturnUrl');
        sessionStorage.setItem('signing_in', '1');
        redirectTo('/onboarding');
        return;
      }

      // Check for pending share link token
      const pendingShareToken = localStorage.getItem('pendingShareToken');
      if (pendingShareToken) {
        console.log('[Auth] Pending share token found, checking user status...');

        // Fast check: does this user have a company? (~50ms DB query vs 500-1500ms Edge Function)
        const { data: profile } = await supabase
          .from('users')
          .select('company_id')
          .eq('id', user.id)
          .maybeSingle();

        if (!profile?.company_id) {
          // New user — skip Edge Function, go straight to onboarding
          // Token stays in localStorage; onboarding will redeem it after completion
          console.log('[Auth] New user with pending share token, redirecting to onboarding');
          localStorage.removeItem('authReturnUrl');
          redirectTo('/onboarding');
          return;
        }

        // Existing user with a company — redeem the share token now
        console.log('[Auth] Existing user, redeeming share token...');
        localStorage.removeItem('pendingShareToken');
        localStorage.removeItem('pendingShareMeta');
        localStorage.removeItem('authReturnUrl');
        try {
          const { data, error } = await supabase.functions.invoke('redeem-menu-share-token', {
            body: { token: pendingShareToken },
          });
          if (!error && data?.outcome) {
            if (data.outcome === 'self_redemption') {
              queueDeferredToast({
                title: 'This is your own share link',
                description: 'Share this link with buyers to give them access to your menu.',
              });
              redirectTo(`/menu/${data.supplier_company_id}/${data.menu_id}`);
              return;
            }
            if (data.outcome === 'direct_access' || data.outcome === 'auto_granted') {
              redirectTo(`/menu/${data.supplier_company_id}/${data.menu_id}`);
              return;
            }
            if (data.outcome === 'request_submitted') {
              redirectTo(`/menu-access-request/${data.request_id}`);
              return;
            }
            if (data.outcome === 'request_rejected') {
              queueDeferredToast({
                title: 'Access request was not approved',
                description: `Your previous request to view this menu was not approved. Please contact ${data.supplier_name || 'the sender'} for more information.`,
                variant: 'destructive',
              });
              redirectTo('/supplier-menus');
              return;
            }
          }
          if (error || data?.error) {
            console.error('[Auth] Share token redemption error:', error || data?.error);
            queueDeferredToast({
              title: 'Unable to process invitation',
              description: data?.error || 'The invitation link may have expired. Please contact the sender.',
              variant: 'destructive',
            });
          }
        } catch (err) {
          console.error('[Auth] Share token redemption failed:', err);
          queueDeferredToast({
            title: 'Something went wrong',
            description: 'We could not process your invitation. Please try again.',
            variant: 'destructive',
          });
        }
        redirectTo('/supplier-menus');
        return;
      }
      
      // Normal sign-in: decide onboarding before touching protected routes.
      // This prevents newly re-invited users with blank profiles/no company
      // from briefly landing on a protected page and getting signed out.
      const gateData = await queryClient.fetchQuery({
        queryKey: onboardingGateQueryKey(user.id),
        queryFn: () => onboardingGateQueryFn(user.id),
        staleTime: 5 * 60 * 1000,
      });

      if (!gateData.onboardingComplete) {
        localStorage.removeItem('authReturnUrl');
        sessionStorage.setItem('signing_in', '1');
        console.log('[Auth] Incomplete onboarding detected, routing to onboarding');
        redirectTo('/onboarding');
        return;
      }

      // Onboarded sign-in: run bootstrap queries + chunk preloads in parallel
      // so ProtectedRoute and AppHeader find cached data immediately.
      const personaPromise = supabase
        .from('users')
        // Explicit FK: users↔companies has several relationships, so a bare embed is
        // ambiguous (PGRST201) and the persona silently resolved to null.
        .select('company_id, companies!users_company_id_fkey(persona)')
        .eq('id', user.id)
        .maybeSingle();

      // Fire all prefetches in parallel (non-blocking for navigation decision)
      const bootstrapPromises = [
        queryClient.prefetchQuery({
          queryKey: headerDataQueryKey(user.id),
          queryFn: () => headerDataQueryFn(user.id),
        }),
        queryClient.prefetchQuery({
          queryKey: ['my-plan', user.id],
          queryFn: async () => {
            const { data } = await supabase.rpc('get_my_plan');
            return data;
          },
          staleTime: 60_000,
        }),
        queryClient.prefetchQuery({
          queryKey: ['userPersonas', user.id],
          queryFn: async () => {
            const { data: userData } = await supabase
              .from('users')
              .select('company_id')
              .eq('id', user.id)
              .maybeSingle();
            if (!userData?.company_id) return [];
            const { data } = await supabase
              .from('companies')
              .select('id, persona')
              .eq('id', userData.company_id)
              .maybeSingle();
            return data
              ? [{
                  id: data.id,
                  user_id: user.id,
                  persona: data.persona as PersonaType,
                  is_primary: true,
                  created_at: new Date().toISOString(),
                }]
              : [];
          },
          staleTime: 5 * 60 * 1000,
        }),
        queryClient.prefetchQuery({
          queryKey: ['systemAdmin', user.id],
          queryFn: async () => {
            const { data } = await supabase
              .from('system_admins')
              .select('id')
              .eq('user_id', user.id)
              .maybeSingle();
            return !!data;
          },
          staleTime: 5 * 60 * 1000,
        }),
      ];

      // Wait for persona (needed to decide destination) + fire bootstrap
      const [personaResult] = await Promise.all([
        personaPromise,
        ...bootstrapPromises,
      ]);

      const primaryPersona = (personaResult.data?.companies as { persona?: PersonaType } | null)?.persona;
      /* Sellers and power traders always land on Your Desk — their daily trigger.
         Desk Setup guides a brand-new seller to their first listing from there. */
      const hasInventory = isSellerPersona(primaryPersona)
        ? await companyHasInventory(personaResult.data?.company_id)
        : null;
      const graduated = primaryPersona === 'buyer' ? await deskSetupGraduated() : null;
      const personaLanding = resolveLandingPath(primaryPersona, hasInventory, graduated);
      // A stored '/my-listings' is a legacy default artifact, not a real destination the
      // person asked for — never let it override the persona landing rule.
      const explicitStoredUrl = storedUrl && storedUrl !== '/my-listings' ? storedUrl : null;
      const returnUrl = explicitStoredUrl || queryReturnUrl || personaLanding;

      // Preload the destination route chunk so Suspense doesn't flash
      const chunkMap: Record<string, () => Promise<unknown>> = {
        '/my-listings': () => import('./Dashboard'),
        '/desk': () => import('./YourDesk'),
        '/my-menu': () => import('./MyMenu'),
        '/supplier-menus': () => import('./SupplierMenus'),
      };
      const preloader = chunkMap[returnUrl];
      if (preloader) await preloader().catch(() => {});

      localStorage.removeItem('authReturnUrl');
      sessionStorage.setItem('signing_in', '1');
      console.log('[Auth] Navigating to:', returnUrl);
      redirectTo(returnUrl);
    };

    checkUserAndRedirect();
  }, [user, loading, initializing, queryReturnUrl]);

  const handleGoogleSignIn = async () => {
    if (consentRequired) markPendingLegalConsent(invitationToken ? 'invite' : 'signup');
    setIsRedirecting(true);
    try {
      // No implicit default: a stored '/my-listings' would override the persona landing rule.
      await signInWithGoogle(queryReturnUrl || undefined);
    } catch (error) {
      setIsRedirecting(false);
      console.error('Failed to sign in');
    }
  };

  const handleMicrosoftSignIn = async () => {
    if (consentRequired) markPendingLegalConsent(invitationToken ? 'invite' : 'signup');
    setIsRedirecting(true);
    try {
      // No implicit default: a stored '/my-listings' would override the persona landing rule.
      await signInWithMicrosoft(queryReturnUrl || undefined);
    } catch (error) {
      setIsRedirecting(false);
      console.error('Failed to sign in with Microsoft');
    }
  };






  if (loading || initializing || user || isRedirecting || shouldHoldCallbackLoader) {
    return <AuthLoadingSkeleton variant="signing-in" />;
  }

  sessionStorage.removeItem('signing_in');

  return (
    <div className="min-h-screen flex flex-col bg-background px-8">
      <SEO 
        title={isSignupIntent ? 'Create your free account - cannafax.ai' : 'Sign In - cannafax.ai'}
        description={
          isSignupIntent
            ? 'Create a free cannafax.ai account — a private network for bulk cannabis inventory.'
            : 'Sign in to Cannafax — your private network for bulk inventory. No marketplace, no chaos.'
        }
        canonical="https://cannafax.ai/auth"
        url="https://cannafax.ai/auth"
      />
      
      <div className="flex-1 flex-col pb-8 flex items-center justify-center">
        <Link to="/" aria-label="Go to cannafax.ai homepage" className="mb-12 inline-block transition-opacity hover:opacity-80">
          <img src={cannafaxLogo} alt="cannafax.ai" className="h-12 w-auto object-contain" />
        </Link>
        <h1 className="font-sans font-normal text-center text-2xl sm:text-3xl lg:text-4xl max-w-sm sm:max-w-lg lg:max-w-2xl text-primary mt-8 sm:mt-12">
          <span className="block"><span className="font-light">Your Private Network for </span><span className="font-bold">Bulk Inventory</span></span>
          <span className="block mt-4 font-bold">—No Marketplace, No Chaos.</span>
        </h1>
      </div>

      <div className="flex-1 flex flex-col items-center justify-start">
        <div className="w-full max-w-sm">
          {invitationToken ? (
            <p className="text-muted-foreground font-sans font-normal text-lg text-center mb-6">
              Sign in to accept your invitation
            </p>
          ) : isSignupIntent ? (
            <>
              <p className="text-muted-foreground font-sans font-normal text-lg text-center mb-6">
                {selectedPlan
                  ? `Create your account to continue with ${selectedPlan.name}`
                  : 'Create your free account to unlock full access'}
              </p>
              {selectedPlan && (
                <div
                  data-testid="auth-selected-plan"
                  className="mb-5 flex items-start justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Selected plan</p>
                    <p className="mt-0.5 text-sm font-semibold text-foreground break-words">
                      {selectedPlan.name}
                      <span className="font-normal text-muted-foreground"> · {formatPlanPrice(selectedPlan).amount}{formatPlanPrice(selectedPlan).suffix ? ` ${formatPlanPrice(selectedPlan).suffix}` : ''}</span>
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {/* The buyer plan is free permanently — no trial language; launch
                          notice only rides along on priced plans. */}
                      {(selectedPlan.price_cents ?? 0) > 0 ? `${LAUNCH_BADGE_LABEL} · ${LAUNCH_NOTICE}` : FREE_FOREVER_LABEL}
                    </p>
                  </div>
                  <Link to="/pricing" className="shrink-0 text-xs font-medium text-primary underline-offset-4 hover:underline">
                    Change plan
                  </Link>
                </div>
              )}
              {referralOfferFrom && isBuyerInvite && (
                <p className="mb-5 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-foreground break-words">
                  {referralOfferFrom} invited you to share inventory through Cannafax.
                </p>
              )}
              {referralOfferFrom && !isBuyerInvite && (
                <div className="invitation-border mb-5 overflow-hidden rounded-lg">
                  <div className="flex items-start gap-3 px-5 pt-5">
                    <Gift className="h-5 w-5 mt-0.5 shrink-0 text-invitation" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-brand-text">Guest Pass</p>
                      <p className="mt-1 text-sm font-medium text-foreground break-words">Courtesy of {referralOfferFrom}</p>
                    </div>
                  </div>
                  <div className="px-5 pb-5 pt-4">
                    <p className="text-2xl font-semibold text-foreground">60 days free</p>
                    <p className="mt-1 text-sm text-muted-foreground">On your first paid plan. Your invitation carries through when you join.</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground font-sans font-normal text-lg text-center mb-6">
              Welcome back — sign in to continue
            </p>
          )}


          <Button
            variant="google"
            size="lg"
            className="w-full"
            onClick={handleGoogleSignIn}
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2 rounded-sm">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {consentRequired ? 'Agree and proceed' : 'Continue with Google'}
          </Button>

          <Button
            variant="google"
            size="lg"
            className="w-full mt-3"
            onClick={handleMicrosoftSignIn}
          >
            <svg viewBox="0 0 23 23" className="w-5 h-5 mr-2" fill="currentColor">
              <path d="M1 1h10v10H1z" />
              <path d="M12 1h10v10H12z" />
              <path d="M1 12h10v10H1z" />
              <path d="M12 12h10v10H12z" />
            </svg>
            {consentRequired ? 'Agree and proceed' : 'Continue with Microsoft'}
          </Button>






          <p className="mt-6 text-xs text-muted-foreground text-center">
            By continuing you agree, on behalf of your organization, to the{' '}
            <Link to="/customer-agreement" target="_blank" className="underline hover:text-foreground transition-colors">
              Customer Agreement
            </Link>
            {', '}
            <Link to="/terms" target="_blank" className="underline hover:text-foreground transition-colors">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link to="/privacy" target="_blank" className="underline hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            .{consentRequired
              ? ' Your acceptance is recorded with the document version, date and time.'
              : ''}
          </p>

          {!invitationToken && (
            <p className="mt-6 text-sm text-muted-foreground text-center">
              {isSignupIntent ? (
                <>
                  Already have an account?{' '}
                  <Link to="/auth" className="underline hover:text-foreground transition-colors">
                    Sign in
                  </Link>
                </>
              ) : (
                <>
                  New to Cannafax?{' '}
                  <Link to="/auth?mode=signup" className="underline hover:text-foreground transition-colors">
                    Start free
                  </Link>
                </>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;
